import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Stack, Typography, Chip, Paper, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, CircularProgress,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ReportLayout, exportToCsv, useReportDates } from './ReportLayout';
import { reportsApi } from '../../api/reports';
import { useNavigate } from 'react-router-dom';

// ─── Types ───────────────────────────────────────────────────────────────────
interface PartRow {
  garment_part: string;
  cut: number;
  stitched: number;
  barcoded: number;
  pending: number;
}

interface DesignRow {
  design_no: string;
  design_id: number | null;
  image_url: string | null;
  product_type: string | null;
  cutting_date: string | null;
  aging_days: number | null;
  total_cut: number;
  total_stitched: number;
  total_barcoded: number;
  total_pending: number;
  parts: PartRow[];
}

interface ChartEntry {
  month: string;
  pending: number;
}

// ─── Sort types ───────────────────────────────────────────────────────────────
type SortKey = 'design_no' | 'aging_days' | 'cut' | 'stitched' | 'barcoded' | 'pending';
type SortDir = 'asc' | 'desc';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function agingColor(days: number): 'success' | 'warning' | 'error' {
  if (days <= 30) return 'success';
  if (days <= 45) return 'warning';
  return 'error';
}

function fmtMonth(mk: string): string {
  const [y, m] = mk.split('-');
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
}

function partSum(design: DesignRow, field: keyof PartRow): number {
  return design.parts.reduce((s, p) => s + (p[field] as number), 0);
}

function sortValue(d: DesignRow, key: SortKey): number | string {
  switch (key) {
    case 'design_no':  return d.design_no;
    case 'aging_days': return d.aging_days ?? 0;
    case 'cut':        return partSum(d, 'cut');
    case 'stitched':   return partSum(d, 'stitched');
    case 'barcoded':   return partSum(d, 'barcoded');
    case 'pending':    return d.total_pending;
  }
}

function sortDesigns(designs: DesignRow[], key: SortKey, dir: SortDir): DesignRow[] {
  return [...designs].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    let cmp = 0;
    if (typeof av === 'string' && typeof bv === 'string') {
      cmp = av.localeCompare(bv);
    } else {
      cmp = (av as number) - (bv as number);
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

// ─── Sortable header cell ─────────────────────────────────────────────────────
interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  align?: 'left' | 'right';
  width?: number;
  onSort: (key: SortKey) => void;
  sx?: object;
}
function SortHeader({ label, sortKey, currentKey, dir, align = 'right', width, onSort, sx }: SortHeaderProps) {
  const active = sortKey === currentKey;
  return (
    <TableCell align={align} sx={{ width, fontWeight: 700, ...sx }}>
      <TableSortLabel
        active={active}
        direction={active ? dir : 'asc'}
        onClick={() => onSort(sortKey)}
        sx={{ flexDirection: align === 'right' ? 'row-reverse' : 'row' }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function StitchingPendingReport() {
  const { startDate, endDate, setStartDate, setEndDate } = useReportDates();
  const navigate = useNavigate();
  const theme = useTheme();

  const [rows, setRows] = useState<DesignRow[]>([]);
  const [chartData, setChartData] = useState<ChartEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [sortKey, setSortKey] = useState<SortKey>('pending');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportsApi.getStitchingPending({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      });
      const payload = res.data;
      setRows(payload.rows ?? []);
      setChartData(payload.chart_data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]); // eslint-disable-line

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // ── Sort then flatten ──────────────────────────────────────────────────────
  interface FlatRow {
    id: string;
    design: DesignRow;
    part: PartRow;
    isFirst: boolean;
    isLast: boolean;
    partCount: number;
  }

  const sortedRows = sortDesigns(rows, sortKey, sortDir);

  const flatRows: FlatRow[] = sortedRows.flatMap((design) =>
    design.parts.map((part, idx) => ({
      id: `${design.design_no}__${part.garment_part}`,
      design,
      part,
      isFirst: idx === 0,
      isLast: idx === design.parts.length - 1,
      partCount: design.parts.length,
    }))
  );

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totalCut      = rows.reduce((s, d) => s + partSum(d, 'cut'), 0);
  const totalStitched = rows.reduce((s, d) => s + partSum(d, 'stitched'), 0);
  const totalBarcoded = rows.reduce((s, d) => s + partSum(d, 'barcoded'), 0);
  const totalPending  = rows.reduce((s, d) => s + d.total_pending, 0);

  const fmtChartData = chartData.map((d) => ({ ...d, label: fmtMonth(d.month) }));

  const sortHeaderProps = { currentKey: sortKey, dir: sortDir, onSort: handleSort };
  const divider = theme.palette.divider;
  const thickBorder = `2px solid ${divider}`;
  const thinBorder  = `1px solid ${divider}`;

  return (
    <ReportLayout
      title="Stitching Pending Report"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onExport={() =>
        exportToCsv('stitching-pending.csv', rows.flatMap((r) =>
          r.parts.map((p) => ({
            design_no: r.design_no,
            product_type: r.product_type ?? '',
            cutting_date: r.cutting_date ?? '',
            aging_days: r.aging_days ?? '',
            garment_part: p.garment_part,
            cut: p.cut,
            stitched: p.stitched,
            barcoded: p.barcoded,
            pending: p.pending,
          }))
        ))
      }
      loading={loading}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Bar chart ─────────────────────────────────────────────── */}
          {fmtChartData.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Month-wise Pending (by Cutting Date)
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={fmtChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={45} />
                  <RechartsTooltip />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} formatter={() => 'Pending'} />
                  <Bar dataKey="pending" name="pending" fill={theme.palette.warning.main} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* ── Summary chips ──────────────────────────────────────────── */}
          {rows.length > 0 && (
            <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`${rows.length} Designs`} variant="outlined" />
              <Chip label={`Cut: ${totalCut}`} color="info" variant="outlined" />
              <Chip label={`Stitched: ${totalStitched}`} color="success" variant="outlined" />
              <Chip label={`Barcoded: ${totalBarcoded}`} color="primary" variant="outlined" />
              <Chip label={`Pending: ${totalPending}`} color="warning" />
            </Stack>
          )}

          {/* ── Table ─────────────────────────────────────────────────── */}
          {rows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No stitching pending designs found for the selected period.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <SortHeader
                      label="Design"
                      sortKey="design_no"
                      align="left"
                      width={200}
                      sx={{ borderRight: thinBorder }}
                      {...sortHeaderProps}
                    />
                    <SortHeader
                      label="Aging"
                      sortKey="aging_days"
                      align="right"
                      width={68}
                      sx={{ borderRight: thinBorder }}
                      {...sortHeaderProps}
                    />
                    <TableCell sx={{ width: 88, fontWeight: 700 }}>Part</TableCell>
                    <SortHeader label="Cut"      sortKey="cut"      width={76}  {...sortHeaderProps} />
                    <SortHeader label="Stitched"  sortKey="stitched" width={82}  {...sortHeaderProps} />
                    <SortHeader label="Barcoded"  sortKey="barcoded" width={82}  {...sortHeaderProps} />
                    <SortHeader label="Pending"   sortKey="pending"  width={82}  {...sortHeaderProps} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flatRows.map((fr) => {
                    const { design, part } = fr;
                    const rowBorder = fr.isLast ? thickBorder : thinBorder;

                    return (
                      <TableRow key={fr.id} sx={{ verticalAlign: 'middle', bgcolor: 'background.paper' }}>

                        {/* ── Col 1: Design — rowSpan across all parts ── */}
                        {fr.isFirst && (
                          <TableCell
                            rowSpan={fr.partCount}
                            sx={{
                              width: 200,
                              verticalAlign: 'top',
                              borderRight: thinBorder,
                              borderBottom: thickBorder,
                              pt: 1, pb: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <Box sx={{ cursor: 'pointer', flexShrink: 0 }}>
                                {design.image_url ? (
                                  <img
                                    src={design.image_url}
                                    alt={design.design_no}
                                    style={{ width: 44, height: 62, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee', display: 'block' }}
                                  />
                                ) : (
                                  <Box sx={{ width: 44, height: 62, borderRadius: 1, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: '0.6rem' }}>
                                    N/A
                                  </Box>
                                )}
                              </Box>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{ display: 'block', cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' }, wordBreak: 'break-word', lineHeight: 1.3 }}
                                  onClick={() => navigate(`/designs?search=${encodeURIComponent(design.design_no)}&preview=true`)}
                                >
                                  {design.design_no}
                                </Typography>
                                {design.product_type && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, wordBreak: 'break-word' }}>
                                    {design.product_type}
                                  </Typography>
                                )}
                                {design.cutting_date && (
                                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1.2 }}>
                                    {new Date(design.cutting_date).toLocaleDateString('en-IN')}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>
                        )}

                        {/* ── Col 2: Aging — rowSpan ── */}
                        {fr.isFirst && (
                          <TableCell
                            rowSpan={fr.partCount}
                            align="right"
                            sx={{
                              width: 68,
                              verticalAlign: 'middle',
                              borderRight: thinBorder,
                              borderBottom: thickBorder,
                            }}
                          >
                            {design.aging_days !== null ? (
                              <Chip
                                label={`${design.aging_days}d`}
                                color={agingColor(design.aging_days)}
                                size="small"
                                variant="filled"
                                sx={{ fontWeight: 700, height: 18, fontSize: '0.65rem' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                        )}

                        {/* ── Part + numbers ── */}
                        <TableCell sx={{ width: 88, borderBottom: rowBorder, py: 0.75 }}>
                          <Chip
                            label={part.garment_part}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.68rem', height: 20 }}
                          />
                        </TableCell>

                        <TableCell align="right" sx={{ width: 76, borderBottom: rowBorder, py: 0.75 }}>
                          <Typography variant="body2">{part.cut}</Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ width: 82, borderBottom: rowBorder, py: 0.75 }}>
                          <Typography variant="body2">{part.stitched}</Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ width: 82, borderBottom: rowBorder, py: 0.75 }}>
                          <Typography variant="body2">{part.barcoded}</Typography>
                        </TableCell>

                        <TableCell align="right" sx={{ width: 82, borderBottom: rowBorder, py: 0.75 }}>
                          {part.pending > 0
                            ? <Chip label={part.pending} color="warning" size="small" variant="filled" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </ReportLayout>
  );
}
